import { type NamedCharacter, stageWords } from "./xiezi-stage-words.js";
import { type DrillProgress, isFinished } from "../handwriting/xiezi-stages.js";

/**
 * Writing the line over a drill square, and replaying the animations that
 * draw the eye to it. `xiezi-view.ts` calls it on every move, and
 * `modules/cidian-xiezi` has the animations.
 */

/**
 * The furthest stage each line has shown each character in, so a stage is
 * announced the first time a character reaches it and not each time an
 * Exercise comes back round to it.
 */
const stagesShown = new WeakMap<HTMLElement, Map<string, number>>();

/** Take an attribute off, settled, and put it back, which replays its animation. */
export function replay(element: HTMLElement, attribute: string): void {
  element.removeAttribute(attribute);
  element.getBoundingClientRect();
  element.setAttribute(attribute, "");
}

/** Whether the character has moved to a stage this line has not shown it in. */
function isNewStage(
  line: HTMLElement,
  progress: DrillProgress,
  character: string,
): boolean {
  const shown = stagesShown.get(line) ?? new Map<string, number>();
  const before = shown.get(character);

  stagesShown.set(line, shown);
  shown.set(character, Math.max(before ?? 0, progress.stage));

  return before !== undefined && progress.stage > before;
}

/**
 * Write the line over the square: what to do in bold, then the stage and pass.
 */
export function showStage(
  line: HTMLElement,
  progress: DrillProgress,
  named: NamedCharacter,
): void {
  const { task, place } = stageWords(progress, named);
  const strong = line.ownerDocument.createElement("strong");
  const where = line.ownerDocument.createElement("span");

  strong.className = "cidian-xiezi-task";
  strong.textContent = task;
  where.className = "cidian-xiezi-place";
  where.textContent = place;
  line.replaceChildren(strong, where);
  line.removeAttribute("data-cb-xiezi-done");

  if (isFinished(progress)) {
    replay(line, "data-cb-xiezi-done");
  } else if (isNewStage(line, progress, named.character)) {
    replay(strong, "data-cb-xiezi-new");
  }
}
